import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";

export type DropZonePosition = "top" | "bottom" | "left" | "right" | "center";

interface DropZoneOverlayProps {
	panelId: string;
	position: DropZonePosition;
	isActive?: boolean;
	children?: React.ReactNode;
}

export const DropZoneOverlay: React.FC<DropZoneOverlayProps> = ({
	panelId,
	position,
	isActive = false,
	children,
}) => {
	const droppableId = `${panelId}-${position}`;
	const { isOver, setNodeRef } = useDroppable({
		id: droppableId,
		data: {
			panelId,
			position,
			type: "split-zone",
		},
	});

	const getPositionStyles = (): string => {
		switch (position) {
			case "top":
				return "absolute inset-x-0 top-0 h-1/3";
			case "bottom":
				return "absolute inset-x-0 bottom-0 h-1/3";
			case "left":
				return "absolute inset-y-0 left-0 w-1/3";
			case "right":
				return "absolute inset-y-0 right-0 w-1/3";
			case "center":
				return "absolute inset-0";
			default:
				return "";
		}
	};

	const getOverlayStyles = (): string => {
		if (!isActive) return "hidden";
		
		const baseStyles = "pointer-events-auto transition-all duration-200";
		
		if (isOver) {
			switch (position) {
				case "top":
				case "bottom":
					return `${baseStyles} bg-primary/20 border-2 border-primary/40`;
				case "left":
				case "right":
					return `${baseStyles} bg-primary/20 border-2 border-primary/40`;
				case "center":
					return `${baseStyles} bg-accent/30 border-2 border-accent/50`;
				default:
					return baseStyles;
			}
		}
		
		// Show subtle hint when dragging but not over
		return `${baseStyles} bg-transparent hover:bg-muted/10`;
	};

	return (
		<div
			ref={setNodeRef}
			className={cn(
				getPositionStyles(),
				getOverlayStyles(),
				"z-50",
			)}
			data-drop-zone={position}
		>
			{isOver && (
				<div className="flex h-full w-full items-center justify-center">
					<div className="rounded-md bg-background/90 px-3 py-1.5 text-xs font-medium shadow-lg">
						{position === "center" ? "Move tab here" : `Split ${position}`}
					</div>
				</div>
			)}
			{children}
		</div>
	);
};

interface PanelDropZonesProps {
	panelId: string;
	isDragging: boolean;
	showSplitZones?: boolean;
}

export const PanelDropZones: React.FC<PanelDropZonesProps> = ({
	panelId,
	isDragging,
	showSplitZones = true,
}) => {
	if (!isDragging) return null;

	return (
		<div className="pointer-events-none absolute inset-0 z-40">
			{showSplitZones && (
				<>
					<DropZoneOverlay panelId={panelId} position="top" isActive={isDragging} />
					<DropZoneOverlay panelId={panelId} position="bottom" isActive={isDragging} />
					<DropZoneOverlay panelId={panelId} position="left" isActive={isDragging} />
					<DropZoneOverlay panelId={panelId} position="right" isActive={isDragging} />
				</>
			)}
			<DropZoneOverlay panelId={panelId} position="center" isActive={isDragging} />
		</div>
	);
};